import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  Button,
  Paper,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  CircularProgress,
  Alert,
} from '@mui/material';
import { ArrowRight } from 'lucide-react';
import PaymentForm from './PaymentForm';
import CreditPackagesDialog from './CreditPackagesDialog';
import { useDashboard } from '../../../hooks/dashboard/useDashboard';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store';
import {
  fetchOrganiationsById,
  setSelectedOrganisation,
} from '../../../store/slices/organizationSlice';
import { setSelectedOrganizationId } from '../../../store/slices/organizationContextSlice';
import { Organisation } from '../../../types/organization';
import { organizationEndpoints } from '../../../services/api/endpoints/organisation.endpoint';
import PlanDescription from './PlanDescription';
import { logoutUser } from '../../../store/slices/authSlice';
import { useNavigate } from 'react-router-dom';
import { CardIcon } from './CardIcon';

interface PlanType {
  id: string;
  product?: {
    name?: string;
    description?: string;
  };
  unit_amount?: number;
  recurring?: {
    interval?: string;
  };
}
export interface IrenewalCheckout {
  organisationId: string;
  customerId: string;
  email: string;
  firstName: string;
  planId: string;
  isTrialEnabled: boolean;
  companyName: string;
}
interface SubscriptionPlansProps {
  recurrencePlans: PlanType[];
  selectedPlan: string;
  isTrialActive: boolean;
  customerCardDetails?: {
    id?: string;
    brand?: string;
    last4?: string;
    billing_email?: string;
    name?: string;
  };
  stripeCustomerId: string;
  replaceCustomerCard: (
    stripeCustomerId: string,
    newPaymentMethodId: string,
    oldPaymentMethodId: string
  ) => Promise<any>;
  createPaymentMethod: (stripeCustomerId: string, paymentMethodId: string) => Promise<any>;
  getCustomerCardDetails: (stripeCustomerId: string) => Promise<any>;
  onCardDetailsUpdate?: (details: any) => void;
  trialDaysRemaining?: number;
  startImmediateSubscription: (stripeCustomerId: string, priceId: string) => Promise<any>;
  cancelAtPeriodEnd: (stripeCustomerId: string) => Promise<any>;
  renewSubscription: (stripeCustomerId: string) => Promise<any>;
  renewalCheckout: (data: any) => Promise<{ url: string }>;
  subscriptionId: string;
  isLoading?: boolean;
}

const formatCurrency = (amount: number = 0) => `AUD ${(amount / 100).toFixed(2)}`;

const SubscriptionPlans: React.FC<SubscriptionPlansProps> = ({
  recurrencePlans,
  selectedPlan,
  isTrialActive,
  customerCardDetails,
  stripeCustomerId,
  replaceCustomerCard,
  createPaymentMethod,
  getCustomerCardDetails,
  onCardDetailsUpdate,
  trialDaysRemaining,
  startImmediateSubscription,
  cancelAtPeriodEnd,
  renewSubscription,
  renewalCheckout,
  subscriptionId,
  isLoading = false,
}) => {
  const theme = useTheme();
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmChangeDialog, setConfirmChangeDialog] = useState(false);
  const [isStartingSubscription, setIsStartingSubscription] = useState(false);
  const [isCancellingSubscription, setIsCancellingSubscription] = useState(false);
  const [openCreditDialog, setOpenCreditDialog] = useState(false);
  const [startSubscriptionDialog, setStartSubscriptionDialog] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [isRenewingSubscription, setIsRenewingSubscription] = useState(false);
  const [renewDialogOpen, setRenewDialogOpen] = useState(false);
  const [isResubscribing, setIsResubscribing] = useState(false);
  const [resubscribeDialogOpen, setResubscribeDialogOpen] = useState(false);

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { dashboardData } = useDashboard();
  const selectedOrganization = useSelector(
    (state: RootState) => state.organizations.selectedOrganisation
  );
  if (!selectedOrganization) {
    console.error('No organization selected');
    return null;
  }
  const selectedOrganizationId = selectedOrganization?.id;
  const organisationStatus = selectedOrganization?.status;

  const DEFAULT_CREDITS = dashboardData?.credits?.remaining || 0;
  const DEFAULT_TOTAL_CREDITS = dashboardData?.credits?.total || 100;
  const DEFAULT_CREDITS_PERCENT = (DEFAULT_CREDITS / DEFAULT_TOTAL_CREDITS) * 100;

  const selectedPlanData = recurrencePlans.find(plan => plan.id === selectedPlan);

  const today = new Date();
  const expires = new Date(selectedOrganization.subscriptionEndDate || 0);
  const isExpired = !isTrialActive && expires <= today;

  const isCardLoading = isLoading;

  console.log('ORGANISATION STATUS', organisationStatus);
  console.log('IS TRAIL ACTIVE', isTrialActive);
  console.log('IS EXPIRED', isExpired);
  console.log('IS CARD LOADING', isCardLoading);

  const handleOpenEditDialog = () => {
    setConfirmChangeDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
  };

  const handlePaymentSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
    newPaymentMethodId?: string
  ) => {
    e.preventDefault();

    if (!newPaymentMethodId) {
      console.error('Missing payment method information');
      return;
    }

    setIsSubmitting(true);
    try {
      if (!customerCardDetails?.id) {
        // Use createPaymentMethod for new customers or when no card exists
        await createPaymentMethod(stripeCustomerId, newPaymentMethodId);
      } else {
        // If existing card, replace it
        await replaceCustomerCard(stripeCustomerId, newPaymentMethodId, customerCardDetails.id);
      }
      // await replaceCustomerCard(stripeCustomerId, newPaymentMethodId, customerCardDetails.id);

      const updatedCardDetails = await getCustomerCardDetails(stripeCustomerId);
      const rawCardData = updatedCardDetails?.[0];

      const cardDetails = rawCardData
        ? {
            id: rawCardData.id,
            brand: rawCardData.card?.brand,
            last4: rawCardData.card?.last4,
            exp_month: rawCardData.card?.exp_month,
            exp_year: rawCardData.card?.exp_year,
            billing_email: rawCardData.billing_details?.email,
          }
        : undefined;
      if (onCardDetailsUpdate) {
        onCardDetailsUpdate(cardDetails);
      }
      handleCloseEditDialog();
    } catch (error) {
      console.error('Error updating payment method:', error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartSubscription = async () => {
    setStartSubscriptionDialog(false);
    setIsStartingSubscription(true);
    try {
      await startImmediateSubscription(subscriptionId, selectedPlan);
      dispatch(setSelectedOrganizationId(selectedOrganizationId!));
      await dispatch(fetchOrganiationsById(selectedOrganizationId!));
      const org: Organisation = await organizationEndpoints.getSingleOrganisation(
        selectedOrganizationId!
      );
      dispatch(setSelectedOrganisation(org));
      setIsStartingSubscription(false);
    } catch (error) {
      console.error('Error starting subscription:', error);
      setIsStartingSubscription(false);
    } finally {
      setIsStartingSubscription(false);
      dispatch(setSelectedOrganizationId(selectedOrganizationId!));
      await dispatch(fetchOrganiationsById(selectedOrganizationId!));
      const org: Organisation = await organizationEndpoints.getSingleOrganisation(
        selectedOrganizationId!
      );
      dispatch(setSelectedOrganisation(org));
    }
  };

  const handleRenewSubscription = async () => {
    setIsRenewingSubscription(true);
    try {
      await renewSubscription(subscriptionId);
      dispatch(setSelectedOrganizationId(selectedOrganizationId!));
      await dispatch(fetchOrganiationsById(selectedOrganizationId!));
      const org: Organisation = await organizationEndpoints.getSingleOrganisation(
        selectedOrganizationId!
      );
      dispatch(setSelectedOrganisation(org));
      setIsRenewingSubscription(false);
      setRenewDialogOpen(false);
    } catch (error) {
      console.error('Error renewing subscription:', error);
      setIsRenewingSubscription(false);
      setRenewDialogOpen(false);
    } finally {
      setIsRenewingSubscription(false);
      setRenewDialogOpen(false);
      dispatch(setSelectedOrganizationId(selectedOrganizationId!));
      await dispatch(fetchOrganiationsById(selectedOrganizationId!));
      const org: Organisation = await organizationEndpoints.getSingleOrganisation(
        selectedOrganizationId!
      );
      dispatch(setSelectedOrganisation(org));
    }
  };

  const handleCancelSubscription = async () => {
    setIsCancellingSubscription(true);

    try {
      await cancelAtPeriodEnd(subscriptionId);
      dispatch(setSelectedOrganizationId(selectedOrganizationId!));
      await dispatch(fetchOrganiationsById(selectedOrganizationId!));
      const org: Organisation = await organizationEndpoints.getSingleOrganisation(
        selectedOrganizationId!
      );
      dispatch(setSelectedOrganisation(org));
      setIsCancellingSubscription(false);
      setCancelDialogOpen(false);
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      setIsCancellingSubscription(false);
      setCancelDialogOpen(false);
    } finally {
      setIsCancellingSubscription(false);
      setCancelDialogOpen(false);
      dispatch(setSelectedOrganizationId(selectedOrganizationId!));
      await dispatch(fetchOrganiationsById(selectedOrganizationId!));
      const org: Organisation = await organizationEndpoints.getSingleOrganisation(
        selectedOrganizationId!
      );
      dispatch(setSelectedOrganisation(org));
    }
  };

  // Add this handler function
  const handleAddCreditClick = () => {
    setOpenCreditDialog(true);
  };

  const handleResubscribe = async () => {
    setIsResubscribing(true);
    try {
      const sessionUrl = await renewalCheckout({
        organisationId: selectedOrganizationId,
        customerId: stripeCustomerId,
        email: selectedOrganization.client.email,
        firstName: selectedOrganization.client.firstName,
        planId: selectedPlan,
        isTrialEnabled: false,
        companyName: selectedOrganization.companyName,
      });
      console.log('sessionUrl', sessionUrl.url);
      // Redirect to Stripe checkout
      window.location.href = sessionUrl.url;
    } catch (error) {
      console.error('Error creating subscription:', error);
    } finally {
      setIsResubscribing(false);
      setResubscribeDialogOpen(false);
    }
  };

  const handleLogout = () => {
    dispatch(logoutUser());
    navigate('/');
  };
  return (
    <Paper sx={{ p: 5, mb: 4, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
      {selectedPlanData ? (
        <>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}
          >
            <Typography variant="h5" component="h2">
              Your Current Plan
            </Typography>
            {/* {isExpired && <Button onClick={handleLogout}>Logout Mate</Button>} */}
          </Box>
          <Box
            sx={{
              mb: 4,

              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: { md: 4 },
            }}
          >
            <Card
              sx={{
                borderRadius: 2,
                flexBasis: { md: '75%' },
                p: 3,
                backgroundColor: `${theme.palette.background.paper}`,
                boxShadow: 0,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'column' },
                  alignItems: { xs: 'left', md: 'left' },
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ mb: 2 }}>
                  <PlanDescription
                    name={selectedPlanData.product?.name}
                    interval={selectedPlanData.recurring?.interval}
                    amount={formatCurrency(selectedPlanData.unit_amount)}
                    duration={selectedPlanData.recurring?.interval || 'month'}
                    trailDaysRemaining={trialDaysRemaining}
                    isTrialActive={isTrialActive}
                  />
                </Box>
                <Box
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'left',
                    textAlign: 'left',
                    justifyContent: 'left',
                    gap: 2,
                  }}
                >
                  {isExpired ? (
                    <Alert severity="info">
                      <Typography variant="body2" color="text.secondary">
                        Please add a payment method to start your subscription.
                      </Typography>
                    </Alert>
                  ) : isTrialActive ? (
                    customerCardDetails?.id ? (
                      <Button
                        endIcon={<ArrowRight size={16} />}
                        variant="contained"
                        size="large"
                        sx={{
                          mt: 2,
                          fontSize: '1rem',
                          fontWeight: 'bold',
                          borderRadius: '8px',
                        }}
                        disabled={isStartingSubscription}
                        onClick={() => setStartSubscriptionDialog(true)}
                      >
                        {isStartingSubscription ? 'Starting...' : 'Start Subscription'}
                      </Button>
                    ) : (
                      <Alert severity="info">
                        <Typography variant="body2" color="text.secondary">
                          Please add a payment method to start your subscription.
                        </Typography>
                      </Alert>
                    )
                  ) : organisationStatus === 'awaiting_payment' ? (
                    <Button
                      endIcon={<ArrowRight size={16} />}
                      variant="contained"
                      size="large"
                      sx={{
                        mt: 2,
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        borderRadius: '8px',
                      }}
                      onClick={() => setRenewDialogOpen(true)}
                      disabled={isRenewingSubscription}
                    >
                      {isRenewingSubscription ? 'Renewing...' : 'Renew Subscription'}
                    </Button>
                  ) : organisationStatus === 'deleted' ? (
                    <Button
                      endIcon={<ArrowRight size={16} />}
                      variant="contained"
                      color="primary"
                      size="large"
                      sx={{
                        borderRadius: '50px',
                        px: 4,
                        py: 1.5,
                        fontWeight: 'bold',
                      }}
                      onClick={() => setResubscribeDialogOpen(true)}
                      disabled={isResubscribing}
                    >
                      {isResubscribing ? 'Processing...' : 'Start Subscribing'}
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      color="error"
                      size="large"
                      onClick={() => setCancelDialogOpen(true)}
                      disabled={isCancellingSubscription}
                      sx={{
                        borderRadius: '50px',
                        px: 4,
                        py: 1.5,
                        fontWeight: 'bold',
                        '&:hover': { backgroundColor: 'error.light' },
                      }}
                    >
                      {isCancellingSubscription ? 'Cancelling...' : 'Cancel Subscription'}
                    </Button>
                  )}
                </Box>
              </Box>
            </Card>
            {organisationStatus === 'active' && !isExpired && (
              <Card
                sx={{
                  py: 3,
                  px: 4,
                  borderRadius: 2,
                  flexBasis: { md: '45%' },
                  display: 'flex',
                  flexDirection: 'column',
                  alignContent: 'space-between',
                  gap: 3,
                  backgroundColor: theme.palette.background.default,
                }}
              >
                <Box>
                  <Typography variant="h6" color="text.primary">
                    Credits Remaining
                  </Typography>
                  <Typography variant="h3" color="text.primary">
                    {DEFAULT_CREDITS}
                    <Typography component="span" variant="body1" color="text.secondary">
                      {' / ' + DEFAULT_TOTAL_CREDITS + ' Total'}
                    </Typography>
                  </Typography>
                </Box>
                <Box>
                  <div className="space-y-2 py-7">
                    <div className="w-full h-2 rounded-lg transition-all duration-500 ease-out shadow-inner bg-gray-200">
                      <div
                        className="h-2 rounded-lg"
                        style={{
                          width: `${DEFAULT_CREDITS_PERCENT}%`,
                          backgroundColor: DEFAULT_CREDITS_PERCENT < 20 ? '#ef4444' : '#10b981',
                        }}
                      />
                    </div>
                  </div>
                </Box>
                {organisationStatus === 'active' &&
                  !isExpired &&
                  !isTrialActive &&
                  customerCardDetails?.id && (
                    <Box>
                      <Box>
                        <Button
                          endIcon={<ArrowRight size={16} />}
                          variant="contained"
                          size="large"
                          sx={{
                            fontSize: '1rem',
                            fontWeight: 'bold',
                            borderRadius: '8px',
                          }}
                          onClick={handleAddCreditClick}
                        >
                          Add Credits
                        </Button>
                      </Box>
                    </Box>
                  )}
              </Card>
            )}
          </Box>
        </>
      ) : (
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            Your Current Plan
          </Typography>
          <Typography variant="subtitle1">No plan selected</Typography>
        </Box>
      )}

      <Box>
        <Typography variant="h5" sx={{ mb: 3, mt: 6 }}>
          Payment Method
        </Typography>

        <Card
          sx={{
            borderRadius: 3,
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            width: 'max-content',
            minWidth: 320,
          }}
        >
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 3, px: 4, py: 3 }}>
            {/* Icon */}
            <CardIcon brand={customerCardDetails?.brand} loading={isCardLoading} />
            <Box sx={{ flex: 1 }}>
              {isCardLoading ? (
                <>
                  <Skeleton width={120} height={20} sx={{ mb: 1 }} />
                  <Skeleton width={180} height={16} />
                </>
              ) : (
                <>
                  {/* Brand + last-4 on one line */}
                  <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>
                    {customerCardDetails?.brand?.toUpperCase() ?? 'N/A'} ‑••••{' '}
                    {customerCardDetails?.last4 ?? '----'}
                  </Typography>

                  {/* Name */}
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                    {customerCardDetails?.name ?? 'Cardholder name not provided'}
                  </Typography>

                  {/* Email */}
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                    {customerCardDetails?.billing_email ?? 'No billing e-mail'}
                  </Typography>
                </>
              )}
            </Box>

            <Button
              variant="contained"
              size="small"
              onClick={handleOpenEditDialog}
              disabled={isCardLoading}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Update
            </Button>
          </CardContent>

          <Dialog
            open={confirmChangeDialog}
            onClose={() => setConfirmChangeDialog(false)}
            maxWidth="xs"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                px: 3,
                py: 2,
              },
            }}
          >
            <DialogTitle
              sx={{
                fontWeight: 'bold',
                fontSize: '1.25rem',
                pb: 1,
              }}
            >
              Confirm Payment Method Update
            </DialogTitle>

            <DialogContent>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  lineHeight: 1.75,
                  mb: 2,
                }}
              >
                You’re about to update your default payment method.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mb: 1.5 }}>
                Going forward, all subscription charges — including your monthly or annual payments
                — will be billed to this new card.
                <br /> Please ensure the new payment method is valid and has sufficient funds to
                avoid any disruption in your service.
              </Typography>
            </DialogContent>

            <DialogActions
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                px: 3,
                pb: 2,
                pt: 1,
              }}
            >
              <Button onClick={() => setConfirmChangeDialog(false)} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setConfirmChangeDialog(false);
                  setOpenEditDialog(true);
                }}
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                sx={{ fontWeight: 'bold', px: 3 }}
              >
                Confirm & Continue
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
            <DialogContent>
              <PaymentForm
                selectedPlanDetails={selectedPlanData}
                selectedAddons={[]}
                handlePaymentSubmit={handlePaymentSubmit}
                isCurrentPlan={true}
                isTrialActive={false}
                isSubmitting={isSubmitting}
                isEditing={true}
                customerCardDetails={customerCardDetails}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseEditDialog} disabled={isSubmitting}>
                Cancel
              </Button>
            </DialogActions>
          </Dialog>
          <CreditPackagesDialog
            open={openCreditDialog}
            onClose={() => setOpenCreditDialog(false)}
            stripeCustomerId={stripeCustomerId}
          />
          <Dialog
            open={startSubscriptionDialog}
            onClose={() => setStartSubscriptionDialog(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                px: 3,
                py: 2,
              },
            }}
          >
            <DialogTitle
              sx={{
                fontWeight: 'bold',
                fontSize: '1.25rem',
                pb: 1,
              }}
            >
              Confirm Subscription Start
            </DialogTitle>

            <DialogContent>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  lineHeight: 1.75,
                  mb: 2,
                }}
              >
                You're about to start your <strong>{selectedPlanData.product?.name}</strong>{' '}
                subscription.
              </Typography>

              <Box
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: 2,
                  p: 2,
                  mb: 2,
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Subscription Details:
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Plan:</Typography>
                  <Typography>{selectedPlanData.product?.name}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Price:</Typography>
                  <Typography>
                    {formatCurrency(selectedPlanData.unit_amount)}/
                    {selectedPlanData.recurring?.interval}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Billing Cycle:</Typography>
                  <Typography>
                    First charge today, then {selectedPlanData.recurring?.interval}ly
                  </Typography>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mb: 1.5 }}>
                <strong>Important:</strong> Starting your subscription will immediately charge your
                payment method and end any remaining trial period.
              </Typography>
            </DialogContent>

            <DialogActions
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                px: 3,
                pb: 2,
                pt: 1,
              }}
            >
              <Button
                onClick={() => setStartSubscriptionDialog(false)}
                disabled={isStartingSubscription}
                sx={{ color: theme.palette.text.secondary }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleStartSubscription}
                variant="contained"
                color="primary"
                disabled={isStartingSubscription}
                sx={{ fontWeight: 'bold', px: 3 }}
              >
                {isStartingSubscription ? 'Processing...' : 'Confirm & Start Subscription'}
              </Button>
            </DialogActions>
          </Dialog>
          {/* Cancellation Confirmation Dialog */}
          <Dialog
            open={cancelDialogOpen}
            onClose={() => setCancelDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                px: 3,
                py: 2,
              },
            }}
          >
            <DialogTitle
              sx={{
                fontWeight: 'bold',
                fontSize: '1.25rem',
                pb: 1,
              }}
            >
              Confirm Subscription Cancellation
            </DialogTitle>
            <DialogContent>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  lineHeight: 1.75,
                  mb: 2,
                }}
              >
                Are you sure you want to cancel your subscription?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mb: 1.5 }}>
                Your access will continue until the end of your current billing period. You can
                reactivate your subscription at any time.
              </Typography>
            </DialogContent>
            <DialogActions
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                px: 3,
                pb: 2,
                pt: 1,
              }}
            >
              <Button
                onClick={() => setCancelDialogOpen(false)}
                disabled={isCancellingSubscription}
              >
                Keep Subscription
              </Button>
              <Button
                onClick={handleCancelSubscription}
                variant="contained"
                color="error"
                disabled={isCancellingSubscription}
                sx={{ fontWeight: 'bold', px: 3 }}
              >
                {isCancellingSubscription ? <CircularProgress size={24} /> : 'Confirm Cancellation'}
              </Button>
            </DialogActions>
          </Dialog>
          {/* Renew Subscription Dialog */}
          <Dialog
            open={renewDialogOpen}
            onClose={() => setRenewDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                px: 3,
                py: 2,
              },
            }}
          >
            <DialogTitle
              sx={{
                fontWeight: 'bold',
                fontSize: '1.25rem',
                pb: 1,
              }}
            >
              Confirm Subscription Renewal
            </DialogTitle>
            <DialogContent>
              <Typography
                variant="body1"
                color="text.primary"
                sx={{
                  lineHeight: 1.75,
                  mb: 2,
                }}
              >
                You're about to renew your <strong>{selectedPlanData.product?.name}</strong>{' '}
                subscription.
              </Typography>

              <Box
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: 2,
                  p: 2,
                  mb: 2,
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Subscription Details:
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Plan:</Typography>
                  <Typography>{selectedPlanData.product?.name}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Price:</Typography>
                  <Typography>
                    {formatCurrency(selectedPlanData.unit_amount)}/
                    {selectedPlanData.recurring?.interval}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Billing Cycle:</Typography>
                  <Typography>Renews {selectedPlanData.recurring?.interval}ly</Typography>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mb: 1.5 }}>
                <strong>Important:</strong> Renewing your subscription will immediately restore full
                access to all features. Your payment method on file will be charged according to the
                billing cycle above.
              </Typography>
            </DialogContent>
            <DialogActions
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                px: 3,
                pb: 2,
                pt: 1,
              }}
            >
              <Button onClick={() => setRenewDialogOpen(false)} disabled={isRenewingSubscription}>
                Cancel
              </Button>
              <Button
                onClick={handleRenewSubscription}
                variant="contained"
                disabled={isRenewingSubscription}
                sx={{ fontWeight: 'bold', px: 3 }}
              >
                {isRenewingSubscription ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Confirm Renewal'
                )}
              </Button>
            </DialogActions>
          </Dialog>
          {/* Resubscribing Dialog */}
          <Dialog
            open={resubscribeDialogOpen}
            onClose={() => setResubscribeDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                px: 3,
                py: 2,
              },
            }}
          >
            <DialogTitle
              sx={{
                fontWeight: 'bold',
                fontSize: '1.25rem',
                pb: 1,
              }}
            >
              Confirm New Subscription
            </DialogTitle>
            <DialogContent>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  lineHeight: 1.75,
                  mb: 2,
                }}
              >
                You're about to subscribe to the <strong>{selectedPlanData.product?.name}</strong>{' '}
                plan.
              </Typography>

              <Box
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: 2,
                  p: 2,
                  mb: 2,
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Subscription Details:
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Plan:</Typography>
                  <Typography>{selectedPlanData.product?.name}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Price:</Typography>
                  <Typography>
                    {formatCurrency(selectedPlanData.unit_amount)}/
                    {selectedPlanData.recurring?.interval}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Billing Cycle:</Typography>
                  <Typography>
                    First charge today, then {selectedPlanData.recurring?.interval}ly
                  </Typography>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mb: 1.5 }}>
                <strong>Note:</strong> This will create a new subscription for your organization and
                grant full access immediately after payment.
              </Typography>
            </DialogContent>
            <DialogActions
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                px: 3,
                pb: 2,
                pt: 1,
              }}
            >
              <Button
                onClick={() => setResubscribeDialogOpen(false)}
                disabled={isResubscribing}
                sx={{ color: theme.palette.text.secondary }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleResubscribe}
                variant="contained"
                color="primary"
                disabled={isResubscribing}
                sx={{ fontWeight: 'bold', px: 3 }}
              >
                {isResubscribing ? 'Processing...' : 'Confirm & Subscribe'}
              </Button>
            </DialogActions>
          </Dialog>
        </Card>
      </Box>
    </Paper>
  );
};

export default SubscriptionPlans;
